import { useCallback, useContext, useMemo, useState } from 'react'

import { faCalendarCheck, faCheckCircle, faClipboard, faCopy, faEdit, faPaste, faQuestionCircle, faTimes } from '@fortawesome/pro-solid-svg-icons'
import { useSelector } from 'react-redux'

import { Action, ActionTypes, getActionsClass, GlobalDeleteAction } from '../../../../Components/antd/Tables/DataTable/utils/actions'
import ActionsRow from '../../../../Components/antd/Lists/ActionsRow'
import { firstOrDefault } from '../../../../utils'
import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../../reducers/UserReducer'
import { ColorsPalette } from '../../../../config/colors'
import { InternshipFormModalContext, Types } from '../../../../Providers/Forms/InternshipForm/InternshipFormModal'
import useInternship from '../../../../utils/api/hooks/useInternship'
import { InternshipStates } from '../../../../utils/constants'
import InternshipRejection from '../../../../Components/shared/InternshipsManager/InternshipRejection'
import { canDeleteInternship } from '../../../../utils/entities/internship'
import InternshipValidation from '../../../../Components/shared/ShiftsManager/InternshipValidation'
import LOCAL_STORAGE_KEYS, { localStorage } from '../../../../utils/localStorage'
import { getTimezoneLessMoment } from '../../../../utils/momentjs'

const {
  PRE_ACCEPTED, ACCEPTED, AUTO_ACCEPTED, ACCEPTED_UNMANAGED, CREATED, PENDING, PENDING_NO_SECTOR,
  REFUSED, SCHEDULE_ADDED, SCHEDULE_TO_VALIDATE, SCHEDULE_VALIDATED
} = InternshipStates

const PENDING_ENABLED_STATES = [PRE_ACCEPTED, ACCEPTED, AUTO_ACCEPTED, ACCEPTED_UNMANAGED, REFUSED, SCHEDULE_ADDED]
const ACCEPTED_ENABLED_STATES = [PRE_ACCEPTED, PENDING, AUTO_ACCEPTED, REFUSED]
const REFUSED_ENABLED_STATES = [PRE_ACCEPTED, PENDING, AUTO_ACCEPTED, ACCEPTED, CREATED, PENDING_NO_SECTOR]
const PRE_ACCEPTED_ENABLED_STATES = [PENDING, REFUSED, CREATED, PENDING_NO_SECTOR]
const VALIDATE_ENABLED_STATES = [SCHEDULE_ADDED, SCHEDULE_TO_VALIDATE]

const InternshipToolBar = ({ date, internships = [], onChange, onUnselect }) => {
  const { setFormHandler } = useContext(InternshipFormModalContext)

  const user = useSelector(selectUser)
  const t = useSelector(selectTranslate)

  const [internshipToRefuse, setInternshipToRefuse] = useState(null)
  const [internshipToValidate, setInternshipToValidate] = useState(null)
  const [useClipboard, setUseClipboard] = useState(localStorage.has(LOCAL_STORAGE_KEYS.PLACES_OCCUPANCY_VIEW.CLIPBOARD))

  const internshipHandler = useInternship({ callback: onChange })

  const handleEditClick = useCallback(() => {
    setFormHandler(handler => ({ ...handler, data: firstOrDefault(internships), callback: onChange, type: Types.SINGLE }))
  }, [internships, setFormHandler])

  const handlePaste = useCallback(() => {
    const internships = localStorage.get(LOCAL_STORAGE_KEYS.PLACES_OCCUPANCY_VIEW.CLIPBOARD)
    const earliestInternship = internships.reduce((earliest, current) => {
      return !earliest || getTimezoneLessMoment(current.startDate) < getTimezoneLessMoment(earliest.startDate) ? current : earliest
    }, null)
    const offset = earliestInternship && date ? getTimezoneLessMoment(date).diff(getTimezoneLessMoment(earliestInternship.startDate), 'days') : 0

    internshipHandler.duplicate(internships, offset)
  }, [date, internshipHandler])

  const actions = useMemo(() => {
    const isSingle = internships.length === 1
    const internshipState = isSingle ? internships[0].state : null
    const internship = isSingle ? internships[0] : null

    const enabledActions = {
      standard: canDeleteInternship(user, internships),
      accept: ACCEPTED_ENABLED_STATES.includes(internshipState),
      edit: isSingle && internshipState !== SCHEDULE_VALIDATED,
      pending: PENDING_ENABLED_STATES.includes(internshipState),
      preAccept: PRE_ACCEPTED_ENABLED_STATES.includes(internshipState),
      refuse: REFUSED_ENABLED_STATES.includes(internshipState),
      validate: VALIDATE_ENABLED_STATES.includes(internshipState)
    }

    return [
      new Action({
        title: t('internship_toolbar.buttons.set_as_pending'),
        className: getActionsClass,
        ghost: true,
        icon: faQuestionCircle,
        type: ActionTypes.GLOBAL,
        color: enabledActions.pending ? ColorsPalette.BLUE_ICON : ColorsPalette.LIGHT_GREY,
        disabled: !enabledActions.pending,
        onClick: () => internshipHandler.setAsPending(internship)
      }),
      new Action({
        title: t('internship_toolbar.buttons.accept'),
        className: getActionsClass,
        ghost: true,
        icon: faCheckCircle,
        type: ActionTypes.GLOBAL,
        color: enabledActions.accept ? ColorsPalette.LIGHT_MOSS_GREEN : ColorsPalette.LIGHT_GREY,
        disabled: !enabledActions.accept,
        onClick: () => internshipHandler.accept(internship)
      }),
      new Action({
        title: t('internship_toolbar.buttons.refuse'),
        className: getActionsClass,
        ghost: true,
        icon: faTimes,
        type: ActionTypes.GLOBAL,
        color: enabledActions.refuse ? ColorsPalette.FIRE_BRICK : ColorsPalette.LIGHT_GREY,
        disabled: !enabledActions.refuse,
        onClick: () => setInternshipToRefuse(internship)
      }),
      new Action({
        title: t('internship_toolbar.buttons.duplicate'),
        className: getActionsClass,
        ghost: true,
        icon: faCopy,
        type: ActionTypes.GLOBAL,
        color: ColorsPalette.LIGHT_BLUE,
        onClick: () => internshipHandler.duplicate(internships)
      }),
      new Action({
        title: t('internship_toolbar.buttons.pre_accept'),
        className: getActionsClass,
        ghost: true,
        icon: faCheckCircle,
        type: ActionTypes.GLOBAL,
        color: enabledActions.preAccept ? ColorsPalette.LIGHT_BLUE : ColorsPalette.LIGHT_GREY,
        disabled: !enabledActions.preAccept,
        onClick: () => internshipHandler.preAccept(internship)
      }),
      new Action({
        title: t('internship_toolbar.buttons.validate'),
        className: getActionsClass,
        ghost: true,
        icon: faCalendarCheck,
        type: ActionTypes.GLOBAL,
        color: enabledActions.validate ? ColorsPalette.BLUE_ICON : ColorsPalette.LIGHT_GREY,
        disabled: !enabledActions.validate,
        onClick: () => setInternshipToValidate(internship)
      }),
      new Action({
        title: t('internship_toolbar.buttons.copy'),
        className: getActionsClass,
        ghost: true,
        icon: faCopy,
        type: ActionTypes.GLOBAL,
        color: enabledActions.standard ? ColorsPalette.BLUE_ICON : ColorsPalette.LIGHT_GREY,
        disabled: !enabledActions.standard,
        onClick: () => {
          localStorage.set(LOCAL_STORAGE_KEYS.PLACES_OCCUPANCY_VIEW.CLIPBOARD, internships)
          setUseClipboard(true)
        }
      }),
      new Action({
        title: t('internship_toolbar.buttons.paste'),
        className: getActionsClass,
        ghost: true,
        icon: faPaste,
        type: ActionTypes.GLOBAL,
        color: enabledActions.standard && useClipboard ? ColorsPalette.BLUE_ICON : ColorsPalette.LIGHT_GREY,
        disabled: !enabledActions.standard || !useClipboard,
        onClick: handlePaste
      }),
      new Action({
        title: t('internship_toolbar.buttons.empty_clipboard'),
        className: getActionsClass,
        ghost: true,
        icon: faClipboard,
        type: ActionTypes.GLOBAL,
        color: enabledActions.standard && useClipboard ? ColorsPalette.RED_ICON : ColorsPalette.LIGHT_GREY,
        disabled: !enabledActions.standard || !useClipboard,
        onClick: () => {
          localStorage.remove(LOCAL_STORAGE_KEYS.PLACES_OCCUPANCY_VIEW.CLIPBOARD)
          setUseClipboard(false)
        }
      }),
      new Action({
        title: t('internship_toolbar.buttons.edit'),
        className: getActionsClass,
        ghost: true,
        icon: faEdit,
        type: ActionTypes.GLOBAL,
        color: enabledActions.edit ? ColorsPalette.BLUE_ICON : ColorsPalette.LIGHT_GREY,
        disabled: !enabledActions.edit,
        onClick: handleEditClick
      }),
      new GlobalDeleteAction({
        title: t('internship_toolbar.buttons.delete'),
        disabled: !enabledActions.standard,
        ghost: true,
        onDelete: () => internshipHandler.delete(internships)
      })
    ]
  }, [internships, user, handleEditClick, handlePaste, internshipHandler.delete, useClipboard])

  const handleValidationChange = useCallback((...params) => {
    setInternshipToValidate(params[0])
    onChange(...params)
  }, [onChange])

  return (
    <div className='z-500'>
      <ActionsRow
        actions={actions}
        buttonTitle={t('internship_toolbar.button_title', { numberOfInternships: internships.length })}
        buttonTooltipTitle={t('internship_toolbar.button_tooltip_title')}
        data={internships}
        onUnselect={onUnselect}
      />
      <InternshipRejection
        internship={internshipToRefuse}
        onClose={() => setInternshipToRefuse(null)}
        onRejection={onChange}
      />
      {internshipToValidate && (
        <InternshipValidation
          internship={internshipToValidate}
          onClose={() => setInternshipToValidate(null)}
          onChange={handleValidationChange}
        />
      )}
    </div>
  )
}

export default InternshipToolBar
